import { TextRenderConfig } from "./TextRenderConfig";
import { Browser } from "../../utils/Browser";
import { measureFont } from "./MeasureFont";
import { Texture2D } from "../../resource/Texture2D";
import { TextureFormat } from "../../RenderEngine/RenderEnum/TextureFormat";
import { FilterMode } from "../../RenderEngine/RenderEnum/FilterMode";
import { WrapMode } from "../../RenderEngine/RenderEnum/WrapMode";
import { LayaGL } from "../../layagl/LayaGL";
import { AtlasGrid } from "./AtlasGrid";
import { ILaya } from "../../../ILaya";
import { ColorUtils } from "../../utils/ColorUtils";
const ITALIC_ANGLE = 13;
const ITALIC_SKEW_RATIO = 0.231;
const FONT_OVERHANG_RATIO = 0.08;
export class TextRender {
    constructor(owner) {
        this.fontMap = new Map();
        this.textAtlases = [];
        this.charMap = new Map();
        this.textMap = new Map();
        this.freeRegions = [];
        this.freeIsoTextures = [];
        this.owner = owner;
        let canvas = Browser.createElement("canvas");
        this.canvas = canvas;
        canvas.width = 1024;
        canvas.height = 512;
        if (Browser.isDomSupported) {
            canvas.style.left = "-10000px";
            canvas.style.position = "absolute";
            Browser.document.body.appendChild(canvas);
        }
        this.ctx = canvas.getContext('2d', { willReadFrequently: true });
        ILaya.systemTimer.loop(5000, this, this.GC);
    }
    draw(text, x, y, font, fontSize, bold, italic, color, stroke, strokeColor, charMode, preMeasuredWidth, renderInfo) {
        let hasEmoji = emojiTest.test(text);
        let curFont = this.getFont(font);
        let info = bold ? curFont.bold : curFont.normal;
        let k = fontSize / TextRenderConfig.standardFontSize;
        fontSizeOffX = Math.ceil(info.xoff * k);
        emojiAdjustY = hasEmoji ? Math.ceil(info.eoff * k) : 0;
        fontSizeOffY = Math.ceil(info.yoff * k) + emojiAdjustY;
        fontSizeH = Math.ceil((hasEmoji ? info.ebbxh : info.bbxh) * k);
        fontScale = TextRenderConfig.fontScale;
        let italicDeg = italic ? ITALIC_ANGLE : 0;
        let cacheKey = (curFont.id * 10000) + fontSize + (bold ? "b" : "") + (italic ? "i" : "") + "_";
        let colorNum = ColorUtils.create(color).numColor;
        let tint = true;
        if (tint)
            cacheKey += colorNum + "_";
        if (stroke > 0)
            cacheKey += ColorUtils.create(strokeColor).numColor + "_" + stroke + "_";
        let ctx = this.ctx;
        ctx.font = (bold ? "bold " : "") + fontSize + "px " + font;
        ctx.setTransform(fontScale, 0, 0, fontScale, 0, 0);
        ctx.textBaseline = "middle";
        ctx.fillStyle = tint ? color : 'white';
        if (stroke > 0) {
            ctx.lineJoin = "round";
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = stroke;
        }
        else {
            ctx.lineWidth = 0;
        }
        if (!renderInfo)
            renderInfo = [];
        let drawColor = tint ? 0xffffffff : colorNum;
        if ((charMode || TextRenderConfig.forceSplitRender) && !TextRenderConfig.forceWholeRender) {
            let mat = this.owner._curMat;
            renderInfo.length > 0 && this.freeRenderInfo(renderInfo);
            for (let i = 0, len = text.length; i < len; i++) {
                let cc = text.charAt(i);
                let ccode = cc.charCodeAt(0);
                if (ccode >= 0xD800 && ccode <= 0xDBFF && i + 1 < len)
                    cc += text.charAt(++i);
                let key = cacheKey + cc;
                let ri = this.charMap.get(key);
                if (!ri) {
                    let width = ctx.measureText(cc).width;
                    ri = this.drawOffscreen(ctx, cc, width, fontSize, stroke, italic, true);
                    ri.key = key;
                    ri.isChar = true;
                    this.charMap.set(key, ri);
                }
                ri.ref++;
                renderInfo.push(ri);
                this.owner._inner_drawTexture(ri.tex, ri.tex.id, x + ri.x, y + ri.y, ri.w, ri.h, mat, ri.uv, 1.0, cc.length > 1 ? 0xffffffff : drawColor, italicDeg, true);
                x += ri.advance + stroke;
            }
        }
        else {
            let key = cacheKey + text;
            let ri = this.textMap.get(key);
            ri && ri.ref++;
            renderInfo[0] && this.free(renderInfo[0]);
            if (!ri) {
                if (preMeasuredWidth == null)
                    preMeasuredWidth = ctx.measureText(text).width;
                ri = this.drawOffscreen(ctx, text, preMeasuredWidth, fontSize, stroke, italic, false);
                ri.key = key;
                ri.ref = 1;
                this.textMap.set(key, ri);
            }
            renderInfo[0] = ri;
            this.owner._inner_drawTexture(ri.tex, ri.tex.id, x + ri.x, y + ri.y, ri.w, ri.h, this.owner._curMat, ri.uv, 1.0, drawColor, italicDeg, true);
            for (let i = 1, n = renderInfo.length; i < n; i++)
                this.free(renderInfo[i]);
            renderInfo.length = 1;
        }
        return renderInfo;
    }
    drawOffscreen(ctx, text, width, height, lineWidth, italic, charMode) {
        let margin = height / 3 | 0 + lineWidth;
        let rectX = ((margin - fontSizeOffX - lineWidth) * fontScale | 0) - blockGap;
        let rectY = ((margin - fontSizeOffY - lineWidth) * fontScale | 0) - blockGap;
        let correctionW = height * (FONT_OVERHANG_RATIO + (italic ? ITALIC_SKEW_RATIO : 0));
        let rectW = Math.ceil((width + fontSizeOffX + lineWidth * 2 + correctionW) * fontScale) + blockGap * 2;
        let rectH = Math.ceil((fontSizeH + lineWidth * 2 + 1) * fontScale) + blockGap * 2;
        let needCanvW = Math.min(rectW + Math.ceil(margin * 2 * fontScale), TextRenderConfig.maxCanvasWidth);
        let needCanvH = Math.min(rectH + Math.ceil(margin * 2 * fontScale), TextRenderConfig.maxCanvasWidth);
        if (needCanvW > this.canvas.width || needCanvH > this.canvas.height)
            this.resizeCanvas(ctx, needCanvW, needCanvH);
        ctx.clearRect(0, 0, Math.ceil(needCanvW / fontScale), Math.ceil(needCanvH / fontScale));
        lineWidth > 0 && ctx.strokeText(text, margin, margin + height / 2);
        ctx.fillText(text, margin, margin + height / 2);
        let imgdt = ctx.getImageData(rectX, rectY, rectW, rectH);
        let ri = {
            x: -(fontSizeOffX + lineWidth),
            y: -lineWidth - emojiAdjustY,
            w: (imgdt.width - blockGap * 2) / fontScale,
            h: (imgdt.height - blockGap * 2) / fontScale,
            advance: width,
            uv: new Array(8),
            tex: null,
            region: null,
            ref: 0
        };
        if (imgdt.width > TextRenderConfig.atlasWidth || imgdt.height > TextRenderConfig.atlasWidth
            || !charMode && TextRenderConfig.noAtlas) {
            ri.tex = this.createIsoTexture(imgdt.width, imgdt.height);
            this.setPixelsToTexture(imgdt, ri.tex, 0, 0, ri.uv);
        }
        else {
            ri.region = this.addToAtlas(imgdt.width, imgdt.height);
            ri.tex = ri.region.owner.tex;
            this.setPixelsToTexture(imgdt, ri.tex, ri.region.x, ri.region.y, ri.uv);
        }
        return ri;
    }
    resizeCanvas(ctx, newWidth, newHeight) {
        newWidth = 512 * Math.ceil(newWidth / 512);
        newHeight = 512 * Math.ceil(newHeight / 512);
        let fontStr = ctx.font;
        let fillStyle = ctx.fillStyle;
        let strokeStyle = ctx.strokeStyle;
        let lineWidth = ctx.lineWidth;
        this.canvas.width = newWidth;
        this.canvas.height = newHeight;
        ctx.setTransform(fontScale, 0, 0, fontScale, 0, 0);
        ctx.font = fontStr;
        ctx.textBaseline = "middle";
        ctx.lineJoin = "round";
        ctx.fillStyle = fillStyle;
        ctx.strokeStyle = strokeStyle;
        ctx.lineWidth = lineWidth;
    }
    createIsoTexture(w, h) {
        let i = this.freeIsoTextures.findIndex(t => t != null && t.width >= w && t.width <= w + h && t.height >= h && t.height <= h + h);
        if (i !== -1) {
            let tex = this.freeIsoTextures[i];
            this.freeIsoTextures[i] = null;
            if (++freeIsoTextureNullCnt > 50) {
                this.freeIsoTextures = this.freeIsoTextures.filter(t => t != null);
                freeIsoTextureNullCnt = 0;
            }
            return tex;
        }
        else
            return this.createTextTexture(w, h);
    }
    addToAtlas(w, h) {
        let i = this.freeRegions.findIndex(r => r != null && r.w >= w && r.w <= w + h && r.h >= h && r.h <= h + h);
        if (i !== -1) {
            let region = this.freeRegions[i];
            this.freeRegions[i] = null;
            if (++freeRegionNullCnt > 50) {
                this.freeRegions = this.freeRegions.filter(r => r != null);
                freeRegionNullCnt = 0;
            }
            region.owner.ref++;
            return region;
        }
        let region;
        for (let ele of this.textAtlases) {
            if (region = ele.grid.allocate(w, h)) {
                region.owner = ele;
                ele.ref++;
                return region;
            }
        }
        let size = TextRenderConfig.atlasWidth;
        let atlas = {
            tex: this.createTextTexture(size, size),
            grid: new AtlasGrid(size, size, TextRenderConfig.atlasGridW),
            ref: 1
        };
        this.textAtlases.push(atlas);
        region = atlas.grid.allocate(w, h);
        region.owner = atlas;
        return region;
    }
    createTextTexture(width, height) {
        let tex = new Texture2D(width, height, TextureFormat.R8G8B8A8, false, false, true, true);
        tex.name = "TextTexture";
        tex.setPixelsData(null, true, false);
        tex.lock = true;
        tex.filterMode = FilterMode.Bilinear;
        tex.wrapModeU = WrapMode.Clamp;
        tex.wrapModeV = WrapMode.Clamp;
        return tex;
    }
    setPixelsToTexture(imgdt, tex, x, y, outUv) {
        let data = imgdt.data;
        if (data instanceof Uint8ClampedArray)
            data = new Uint8Array(data.buffer);
        LayaGL.textureContext.setTextureSubPixelsData(tex._texture, data, 0, false, x, y, imgdt.width, imgdt.height, true, false);
        let u0 = (x + blockGap) / tex.width;
        let v0 = (y + blockGap) / tex.height;
        let u1 = (x + imgdt.width - blockGap) / tex.width;
        let v1 = (y + imgdt.height - blockGap) / tex.height;
        outUv[0] = u0, outUv[1] = v0;
        outUv[2] = u1, outUv[3] = v0;
        outUv[4] = u1, outUv[5] = v1;
        outUv[6] = u0, outUv[7] = v1;
    }
    freeRenderInfo(arr) {
        for (let ri of arr) {
            this.free(ri);
        }
        arr.length = 0;
    }
    free(ri) {
        ri.ref--;
        if (ri.ref > 0)
            return;
        if (ri.isChar) {
            if (ri.ref === 0)
                return;
            this.charMap.delete(ri.key);
        }
        else
            this.textMap.delete(ri.key);
        if (ri.region != null) {
            let atlas = ri.region.owner;
            atlas.ref--;
            if (atlas.ref === 0) {
                if (this.freeRegions.length > 0)
                    this.freeRegions = this.freeRegions.filter(r => r != null && r.owner !== atlas);
                freeRegionNullCnt = 0;
                if (this.textAtlases.length > 2 && this.textAtlases.findIndex((a) => a.ref === 0) !== -1) {
                    atlas.tex.destroy();
                    let idx = this.textAtlases.indexOf(atlas);
                    this.textAtlases.splice(idx, 1);
                }
                else {
                    atlas.grid.reset();
                }
            }
            else
                this.freeRegions.push(ri.region);
        }
        else {
            this.freeIsoTextures.push(ri.tex);
        }
    }
    getFontHeight(font, fontSize, bold) {
        let inst = this.getFont(font);
        let k = fontSize / TextRenderConfig.standardFontSize;
        let info = bold ? inst.bold : inst.normal;
        return Math.ceil(info.bbxh * k);
    }
    getFont(font) {
        let inst = this.fontMap.get(font);
        if (inst == null) {
            inst = {
                id: fontIdCounter++,
                normal: measureFont(this.ctx, font, false),
                bold: measureFont(this.ctx, font, true)
            };
            this.fontMap.set(font, inst);
        }
        return inst;
    }
    onFontScaleChanged() {
        this.textMap.clear();
        toClearChars.length = 0;
        for (let [_, ri] of this.charMap) {
            if (ri.ref === 0)
                toClearChars.push(ri);
            else
                ri.ref--;
        }
        for (let ri of toClearChars) {
            this.free(ri);
        }
        this.charMap.clear();
    }
    GC() {
        if (this.freeIsoTextures.length > 0) {
            for (let tex of this.freeIsoTextures)
                tex === null || tex === void 0 ? void 0 : tex.destroy();
            this.freeIsoTextures.length = 0;
            freeIsoTextureNullCnt = 0;
        }
        toClearChars.length = 0;
        for (let [_, ri] of this.charMap) {
            if (ri.ref === 0)
                toClearChars.push(ri);
        }
        for (let ri of toClearChars) {
            this.free(ri);
        }
    }
}
const blockGap = 1;
const emojiTest = /[\uD800-\uDBFF][\uDC00-\uDFFF]/;
var fontIdCounter = 0;
var fontScale = 1.0;
var fontSizeH = 0;
var fontSizeOffX = 0;
var fontSizeOffY = 0;
var emojiAdjustY = 0;
var freeRegionNullCnt = 0;
var freeIsoTextureNullCnt = 0;
var toClearChars = [];
